/**
 * Report Selector Component
 * 
 * Allows users to select which credit report to use for dispute generation.
 * 
 * BEHAVIOR:
 * - Auto-preselects the latest report for convenience
 * - User can switch to an older report
 * - Once confirmed/locked, the selected report is the source of truth
 * - Shows clear messaging about what each choice means
 */

import React, { useState, useEffect } from 'react';
import { useReportData } from '../../context/ReportDataContext';
import { 
  FileText, 
  Calendar, 
  CheckCircle, 
  Lock, 
  Unlock, 
  AlertCircle,
  ChevronDown,
  Sparkles,
  Info,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * Format a date string for display
 */
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Report Option Component - shows a single report option
 */
const ReportOption = ({ report, isSelected, onSelect, disabled }) => {
  const isLatest = report.is_latest;
  
  return (
    <div
      onClick={() => !disabled && onSelect(report.report_id)}
      className={`
        relative p-4 rounded-lg border-2 cursor-pointer transition-all
        ${isSelected 
          ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20' 
          : 'border-stone-200 hover:border-stone-300 dark:border-stone-700 dark:hover:border-stone-600'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      data-testid={`report-option-${report.report_id}`}
    >
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2">
          <CheckCircle className="h-5 w-5 text-emerald-500" />
        </div>
      )}
      
      {/* Latest badge */}
      {isLatest && (
        <Badge className="mb-2 bg-emerald-500 text-white">
          <Sparkles className="h-3 w-3 mr-1" />
          Latest Report (Recommended)
        </Badge>
      )}
      
      {/* Report info */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center">
          <FileText className="h-5 w-5 text-stone-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-stone-800 dark:text-stone-200 truncate">
            {report.file_name || 'Credit Report'}
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(report.created_at)}
          </p>
          
          {/* Scores preview */}
          {report.scores && (
            <div className="flex gap-2 mt-2">
              {report.scores.transunion && (
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded dark:bg-blue-900/30 dark:text-blue-300">
                  TU: {report.scores.transunion}
                </span>
              )}
              {report.scores.experian && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded dark:bg-purple-900/30 dark:text-purple-300">
                  EX: {report.scores.experian}
                </span>
              )}
              {report.scores.equifax && (
                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded dark:bg-red-900/30 dark:text-red-300">
                  EQ: {report.scores.equifax}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Usage guidance */}
      <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">
        {isLatest 
          ? 'Best for most users. Use this to dispute what currently shows on your report.'
          : 'Use this if an earlier report shows items that no longer appear on newer reports.'}
      </p>
    </div>
  );
};

/**
 * Locked Report Banner - shows when report is locked for the round
 */
const LockedReportBanner = ({ lockedInfo, onUnlock }) => {
  return (
    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Lock className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-emerald-800 dark:text-emerald-200">
            Source Report Locked
          </p>
          <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
            This report is locked for this dispute round. All late months, account details, 
            and dispute evidence will be generated from this selected report only.
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <FileText className="h-4 w-4" />
            <span className="font-medium">{lockedInfo?.file_name || 'Selected Report'}</span>
            {lockedInfo?.is_latest && (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                Latest
              </Badge>
            )}
          </div>
          {lockedInfo?.created_at && (
            <p className="text-xs text-emerald-500 mt-1">
              Report date: {formatDate(lockedInfo.created_at)}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onUnlock}
          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
          data-testid="btn-change-report"
        >
          <Unlock className="h-4 w-4 mr-1" />
          Change
        </Button>
      </div>
    </div>
  );
};

/**
 * Main Report Selector Component
 */
const ReportSelector = ({ 
  onReportSelected, 
  onReportLocked,
  showCompact = false,
  className = '',
}) => {
  const {
    availableReports,
    reportData,
    hasReport,
    selectedReportId,
    isReportLocked,
    fetchAvailableReports,
    selectReport,
    lockSelectedReport,
    unlockReport,
    getLockedReportInfo,
    loading,
    listLoading,
  } = useReportData();
  
  const [localLoading, setLocalLoading] = useState(false);
  const reportsLoading = loading || listLoading || localLoading;
  
  // Fetch reports on mount
  useEffect(() => {
    fetchAvailableReports();
  }, [fetchAvailableReports]);

  // Auto-select the active report when data exists but nothing is selected yet
  useEffect(() => {
    if (selectedReportId || isReportLocked || reportsLoading) return;
    const activeReportId = reportData?.report_id || availableReports[0]?.report_id;
    if (activeReportId) {
      selectReport(activeReportId);
      onReportSelected?.(activeReportId);
    }
  }, [
    selectedReportId,
    isReportLocked,
    reportsLoading,
    reportData?.report_id,
    availableReports,
    selectReport,
    onReportSelected,
  ]);
  
  if (process.env.NODE_ENV === 'development') {
    console.debug('[ReportSelector]', {
      hasReport,
      reportDataExists: Boolean(reportData),
      availableReportsLength: availableReports.length,
      selectedReportId,
      reportsLoading,
    });
  }
  
  // Handle report selection
  const handleSelectReport = async (reportId) => {
    if (isReportLocked) return;
    
    setLocalLoading(true);
    try {
      await selectReport(reportId);
      onReportSelected?.(reportId);
    } finally {
      setLocalLoading(false);
    }
  };
  
  // Handle report lock
  const handleLockReport = () => {
    const success = lockSelectedReport();
    if (success) {
      onReportLocked?.(selectedReportId);
    }
  };
  
  // Handle unlock
  const handleUnlock = () => {
    unlockReport();
  };
  
  // If locked, show the locked banner
  if (isReportLocked) {
    const lockedInfo = getLockedReportInfo();
    return (
      <div className={className}>
        <LockedReportBanner lockedInfo={lockedInfo} onUnlock={handleUnlock} />
      </div>
    );
  }
  
  // If no reports, show message only when both list and report-data are truly empty
  if (!hasReport && availableReports.length === 0 && !reportsLoading) {
    return (
      <div className={`p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg ${className}`}>
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <AlertCircle className="h-5 w-5" />
          <span>No credit reports available. Please import a report first.</span>
        </div>
      </div>
    );
  }

  if (reportsLoading && availableReports.length === 0) {
    return (
      <div className={`flex items-center justify-center p-6 ${className}`}>
        <RefreshCw className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }
  
  // Compact mode - dropdown selector
  if (showCompact) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400">
          <Info className="h-4 w-4" />
          <span>Select the source report for this dispute round</span>
        </div>
        
        <Select value={selectedReportId} onValueChange={handleSelectReport}>
          <SelectTrigger className="w-full" data-testid="report-selector-dropdown">
            <SelectValue placeholder="Select a report..." />
          </SelectTrigger>
          <SelectContent>
            {availableReports.map((report) => (
              <SelectItem key={report.report_id} value={report.report_id}>
                <div className="flex items-center gap-2">
                  {report.is_latest && <Sparkles className="h-3 w-3 text-amber-500" />}
                  <span>{report.file_name || 'Credit Report'}</span>
                  <span className="text-xs text-stone-400">
                    ({formatDate(report.created_at)})
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedReportId && (
          <Button 
            onClick={handleLockReport}
            className="w-full bg-emerald-500 hover:bg-emerald-600"
            data-testid="btn-confirm-report"
          >
            <Lock className="h-4 w-4 mr-2" />
            Confirm & Lock This Report
          </Button>
        )}
      </div>
    );
  }
  
  // Full mode - card selector
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with explanation */}
      <div className="bg-stone-50 dark:bg-stone-800/50 rounded-lg p-4 border border-stone-200 dark:border-stone-700">
        <h3 className="font-semibold text-stone-800 dark:text-stone-200 flex items-center gap-2">
          <FileText className="h-5 w-5 text-emerald-500" />
          Source Report for This Dispute
        </h3>
        <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">
          We preselect your latest report to make setup faster. You can switch to an older 
          report if that report better reflects the items you want to dispute.
        </p>
      </div>
      
      {/* Report options */}
      <div className="space-y-3">
        {reportsLoading ? (
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          availableReports.map((report) => (
            <ReportOption
              key={report.report_id}
              report={report}
              isSelected={selectedReportId === report.report_id}
              onSelect={handleSelectReport}
              disabled={isReportLocked}
            />
          ))
        )}
      </div>
      
      {/* Confirm button */}
      {selectedReportId && !isReportLocked && (
        <Button 
          onClick={handleLockReport}
          className="w-full bg-emerald-500 hover:bg-emerald-600"
          size="lg"
          data-testid="btn-confirm-report"
        >
          <Lock className="h-4 w-4 mr-2" />
          Confirm & Lock This Report for Dispute Round
        </Button>
      )}
      
      {/* Info note */}
      <p className="text-xs text-stone-500 dark:text-stone-400 text-center">
        Once locked, all dispute evidence will be generated from this report only.
        You can change the report by clicking "Change" if needed.
      </p>
    </div>
  );
};

export default ReportSelector;
